import {Component, OnInit} from "@angular/core";
import {ILocationActionDialogData, LocationActionComponent,} from "./location-action/location-action.component";
import {MatDialog} from "@angular/material/dialog";
import {NotificationService} from "../../../core/services/notification.service";
import {
  ConfirmationDialogComponent,
  IConfirmationDialogData,
} from "../../../shared/components/confirmation-dialog/confirmation-dialog.component";
import {EDeleteLocationUsersAction} from "../../../shared/enums/EDeleteLocationUsersAction";
import {LocationsControllerService} from "../../../core/api/controllers/locations-controller.service";
import {ILocation} from "../../../shared/interfaces/ILocation";
import {EAction} from "../../../shared/enums/EAction";

@Component({
  selector: "app-locations",
  templateUrl: "./locations.component.html",
  styleUrls: ["./locations.component.scss"],
})
export class LocationsComponent implements OnInit {
  locations: ILocation[] = [];
  isLoading = false;
  searchTerm = "";
  readonly skeletonRows = [0, 1, 2, 3];

  constructor(
    private dialog: MatDialog,
    private notificationService: NotificationService,
    private locationsController: LocationsControllerService,
  ) {}

  ngOnInit(): void {
    this.getLocations();
  }

  get filteredLocations(): ILocation[] {
    const term = this.searchTerm.trim().toLowerCase();
    if (!term) {
      return this.locations;
    }
    return this.locations.filter((location) =>
      [location.name, location.state?.name, location.country?.name]
        .filter(Boolean)
        .some((value) => value.toLowerCase().includes(term)),
    );
  }

  get activeLocationsCount(): number {
    return this.locations.filter((location) => location.isActive).length;
  }

  get disabledLocationsCount(): number {
    return this.locations.length - this.activeLocationsCount;
  }

  clearSearch(): void {
    this.searchTerm = "";
  }

  trackByLocationId(index: number, location: ILocation): string {
    return location.id;
  }

  regionLabel(location: ILocation): string {
    return [location.state?.name, location.country?.name]
      .filter(Boolean)
      .join(", ");
  }

  /** Stable per-location colour so the tiles stay recognisable between refreshes. */
  tileTone(location: ILocation): number {
    const source = location.id || location.name || "";
    let hash = 0;
    for (let i = 0; i < source.length; i++) {
      hash = (hash + source.charCodeAt(i)) % 6;
    }
    return hash;
  }

  addLocation() {
    const data: ILocationActionDialogData = {
      action: EAction.Add,
    };
    this.dialog
      .open(LocationActionComponent, { data, width: "400px" })
      .afterClosed()
      .subscribe((result) => {
        if (result) {
          this.getLocations();
        }
      });
  }

  getLocations() {
    this.isLoading = true;
    this.locationsController.getAllLocations().subscribe(
      (response) => {
        this.locations = response.locations;
        this.locations = this.locations.map((location) => {
          return {
            ...location,
            ips: ["192.168.1.1", "172.192.15.13", "152.145.161.2"],
            collapsed: false,
          };
        });
        this.isLoading = false;
      },
      (error) => {
        this.notificationService.error(error);
        this.isLoading = false;
      },
    );
  }

  editLocation(currentLocation: ILocation) {
    const data: ILocationActionDialogData = {
      action: EAction.Edit,
      location: currentLocation,
    };
    this.dialog
      .open(LocationActionComponent, { data, width: "400px" })
      .afterClosed()
      .subscribe((result) => {
        if (result) {
          this.getLocations();
        }
      });
  }

  handleDeleteLocationModal(locationId) {
    const data: IConfirmationDialogData = {
      title: "Delete location?",
      message: "Are you sure you want to delete this location?",
    };
    this.dialog
      .open(ConfirmationDialogComponent, { data })
      .afterClosed()
      .subscribe((result) => {
        if (result) {
          this.deleteLocation(locationId);
        }
      });
  }

  deleteLocation(
    locationId,
    action = EDeleteLocationUsersAction.NoAction,
    newLocationId?,
  ) {
    this.locationsController
      .deleteLocation(locationId, action, newLocationId)
      .then(() => {
        this.getLocations();

        this.notificationService.success(
          "Location has been deleted" +
            (action === EDeleteLocationUsersAction.MoveUsers
              ? " and users have been moved."
              : "."),
        );
      })
      .catch((error) => this.notificationService.error(error));
  }

  openToggleLocationActive(location: ILocation): void {
    const data: IConfirmationDialogData = {
      title: (location.isActive ? "Disable location" : "Enable location"),
      message: "Are you sure you want to " + (location.isActive ? "disable" : "enable") + ` the location '${location.name}'?`
    }
    this.dialog.open(ConfirmationDialogComponent, {
      data
    }).afterClosed().subscribe(res => {
      if(res) this.toggleLocationActive(location);
    })
  }

  toggleLocationActive(location: ILocation): void {
    this.isLoading = true;
    this.locationsController.changeLocationIsActive(location.id, !location.isActive).subscribe({
      next: () => {
        this.getLocations();
      },
      error: () => {
        this.isLoading = false;
      }
    })
  }
}
